import {
  createCustomAutomationAsync,
  loadDeviceActionsAsync,
} from "@/base/farmhq-api"
import {
  listConfigSensors,
  type SensorName,
} from "@/base/sensor-configurations"
import type { SensorState } from "@/base/sensor-events"
import { SENSOR_NAME_TO_STATES } from "@/base/sensor-events"
import { formatSensorName } from "@/base/sensor-formatting"
import { makeDeviceConfigurationCacheKey } from "@/redux/device-configurations.reducer"
import i18n from "@/translations/i18n"
import type { PayloadAction, Reducer } from "@reduxjs/toolkit"
import { createSlice } from "@reduxjs/toolkit"
import { captureException } from "@sentry/react-native"
import { createCachedSelector } from "re-reselect"
import { createAsyncThunk } from "./create-async-thunk"
import * as Models from "./models"
import type { GetRootState, RootState } from "./root.reducer"
import type { RootDispatch } from "./store"

export interface CreateConnectionFormData {
  delaySeconds: number | null
  sourceDeviceId: string
  sourceSensor: SensorName | ""
  sourceSensorStateCurrent: SensorState | ""
  sourceSensorStatePrevious: SensorState | ""
  targetActionId: number | null
  targetDeviceId: string
}

type Errors = {
  [key in keyof CreateConnectionFormData | "form"]?: string
}

export interface CreateDeviceConnectionState extends CreateConnectionFormData {
  actionOptions?: Models.NamedDeviceAction[]
  showErrors: boolean
}

/**
 * Check properties of form data to see if matching connection already exists
 */
export function getConnectionAlreadyCreated(state: RootState) {
  const allConnections = Models.deviceConnection.selectAll(state)
  const {
    sourceDeviceId,
    sourceSensor,
    sourceSensorStateCurrent,
    sourceSensorStatePrevious,
    targetActionId,
    targetDeviceId,
  } = state.createDeviceConnection
  return Boolean(
    allConnections.find((connection): boolean => {
      if (
        connection.sourceDeviceId === sourceDeviceId &&
        connection.sourceSensor === sourceSensor &&
        connection.sourceSensorStateCurrent === sourceSensorStateCurrent &&
        connection.sourceSensorStatePrevious === sourceSensorStatePrevious &&
        connection.targetDeviceId === targetDeviceId &&
        connection.namedDeviceAction?.id === targetActionId
      ) {
        return true
      }
      return false
    }),
  )
}
function getStateCurrentAndPreviousAreEqual(state: RootState) {
  const { sourceSensorStateCurrent, sourceSensorStatePrevious } =
    state.createDeviceConnection
  if (
    sourceSensorStateCurrent.length > 0 &&
    sourceSensorStatePrevious.length > 0
  ) {
    return sourceSensorStateCurrent === sourceSensorStatePrevious
  }
  return false
}

function validate(state: RootState): Errors {
  const formData = state.createDeviceConnection
  const errors: Errors = {}
  const sourceConfig = Models.deviceConfiguration.selectById(
    state,
    formData.sourceDeviceId,
  )
  const targetConfig = Models.deviceConfiguration.selectById(
    state,
    formData.targetDeviceId,
  )
  if (
    typeof formData.sourceDeviceId === "string" &&
    formData.sourceDeviceId.length > 0
  ) {
    if (sourceConfig?.deviceInstallationType === "unconfigured") {
      errors.sourceDeviceId = i18n.t("errorMessageDeviceIsNotConfigured", {
        ns: "createDeviceConnection",
      })
    }
  } else {
    errors.sourceDeviceId = i18n.t("validation:required")
  }

  // SOURCE SENSOR
  if (!formData.sourceSensor) {
    errors.sourceSensor = i18n.t("validation:required")
  }
  // STATES
  if (getStateCurrentAndPreviousAreEqual(state)) {
    errors.sourceSensorStateCurrent = i18n.t(
      "createDeviceConnection:errorMessageStatesEqual",
    )
  } else {
    if (!formData.sourceSensorStatePrevious) {
      errors.sourceSensorStatePrevious = i18n.t("validation:required")
    }
    if (!formData.sourceSensorStateCurrent) {
      errors.sourceSensorStateCurrent = i18n.t("validation:required")
    }
  }
  // TARGET DEVICE
  if (
    typeof formData.targetDeviceId === "string" &&
    formData.targetDeviceId.length > 0
  ) {
    if (targetConfig?.deviceInstallationType === "unconfigured") {
      errors.targetDeviceId = i18n.t(
        "createDeviceConnection:errorMessageDeviceIsNotConfigured",
      )
    }
  } else {
    errors.targetDeviceId = i18n.t("validation:required")
  }

  // ACTIONS
  const actions = state.createDeviceConnection.actionOptions
  if (Array.isArray(actions) && actions.length === 0) {
    errors.targetActionId = i18n.t(
      "createDeviceConnection:noActionsConfiguredErrorMessage",
    )
  } else if (typeof formData.targetActionId !== "number") {
    errors.targetActionId = i18n.t("validation:required")
  }

  // DELAY SECONDS
  if (
    formData.delaySeconds !== null &&
    typeof formData.delaySeconds === "number"
  ) {
    if (formData.delaySeconds < 60) {
      errors.delaySeconds = i18n.t(
        "createDeviceConnection:delaySecondsMinimumError",
        { defaultValue: "Delay must be at least 60 seconds" },
      )
    } else if (formData.delaySeconds > 86400) {
      errors.delaySeconds = i18n.t(
        "createDeviceConnection:delaySecondsMaximumError",
        { defaultValue: "Delay cannot exceed 24 hours (86400 seconds)" },
      )
    }
  }

  // FORM ERRORS
  if (getConnectionAlreadyCreated(state)) {
    errors.form = i18n.t("createDeviceConnection:errorMessageAlreadyCreated")
  }
  return errors
}

const initialState: CreateDeviceConnectionState = {
  delaySeconds: null,
  showErrors: false,
  sourceDeviceId: "",
  sourceSensor: "",
  sourceSensorStateCurrent: "",
  sourceSensorStatePrevious: "",
  targetActionId: null,
  targetDeviceId: "",
}

const slice = createSlice({
  extraReducers: (builder) => {
    return builder
      .addCase(loadDeviceActionsAsync.pending, (state) => {
        state.actionOptions = undefined

        // state.errors.targetActionId = undefined
      })
      .addCase(loadDeviceActionsAsync.fulfilled, (state, action) => {
        state.actionOptions = action.payload.items
      })
      .addCase(createCustomAutomationAsync.fulfilled, () => {
        return {
          ...initialState,
        }
      })
  },
  initialState,
  name: "createDeviceConnection",
  reducers: {
    resetCreateConnectionsForm: () => {
      return {
        ...initialState,
      }
    },
    setDelaySeconds: (state, action: PayloadAction<number | null>) => {
      state.delaySeconds = action.payload
    },
    setSourceDevice: (state, action: PayloadAction<string>) => {
      state.sourceDeviceId = action.payload
    },
    setSourceSensor: (
      state,
      { payload: sensorName }: PayloadAction<SensorName | "">,
    ) => {
      state.sourceSensor = sensorName
      // Use the same defaults for this sensor as we do for the source device
      // change handler
      switch (sensorName) {
        case "reel":
        case "pressure":
        case "wheel":
        case "flow": {
          const defaultSensorStates = {
            flow: {
              current: "FLN",
              previous: "FLF",
            },
            pressure: {
              current: "PHI",
              previous: "PLO",
            },
            reel: {
              current: "RS",
              previous: "RR",
            },
            wheel: {
              current: "WS",
              previous: "WL",
            },
          } as const
          const { current, previous } = defaultSensorStates[sensorName]
          state.sourceSensorStatePrevious = previous
          state.sourceSensorStateCurrent = current

          break
        }
        default: {
          // By default, we will use the first and second state for the sensor
          if (sensorName) {
            const states = Object.values(SENSOR_NAME_TO_STATES[sensorName])
            state.sourceSensorStatePrevious = states[0] ?? ""
            state.sourceSensorStateCurrent = states[1] ?? ""
          }
          break
        }
      }
    },
    setSourceSensorStateCurrent: (
      state,
      action: PayloadAction<SensorState | "">,
    ) => {
      state.sourceSensorStateCurrent = action.payload
    },
    setSourceSensorStatePrevious: (
      state,
      action: PayloadAction<SensorState | "">,
    ) => {
      state.sourceSensorStatePrevious = action.payload
    },
    setTargetActionId: (state, action: PayloadAction<number | null>) => {
      state.targetActionId = action.payload
    },
    setTargetDevice: (state, action: PayloadAction<string>) => {
      state.actionOptions = undefined
      state.targetDeviceId = action.payload
    },
    showErrors: (state) => {
      state.showErrors = true
    },
  },
})

const createDeviceConnectionReducer: Reducer<CreateDeviceConnectionState> =
  slice.reducer
export default createDeviceConnectionReducer

export const {
  resetCreateConnectionsForm,
  setDelaySeconds,
  setSourceDevice,
  setSourceSensor,
  setSourceSensorStateCurrent,
  setSourceSensorStatePrevious,
  setTargetActionId,
  setTargetDevice,
  showErrors,
} = slice.actions

export function onChangeSourceDevice(deviceId: string) {
  return (dispatch: RootDispatch, getState: GetRootState) => {
    const state = getState()
    dispatch(setSourceDevice(deviceId))

    const config = Models.deviceConfiguration.selectById(state, deviceId)
    if (config) {
      // const sensors = listConfigSensors(config)
      switch (config.deviceInstallationType) {
        case "prototype":
        case "reel_with_booster_off_only":
        case "reel": {
          dispatch(setSourceSensor("reel"))
          break
        }
        case "traveller_soft":
        case "linear_move":
        case "center_pivot": {
          // TODO: Update these to use activity state
          dispatch(setSourceSensor("wheel"))
          break
        }
        case "pump_off_only":
        case "pump_on_off":
        case "pump_vfd":
        case "pump": {
          dispatch(setSourceSensor("pressure"))
          break
        }

        case "valve": {
          dispatch(setSourceSensor("flow"))
          break
        }

        case "unconfigured": {
          dispatch(setSourceSensor(""))
          dispatch(setSourceSensorStatePrevious(""))
          dispatch(setSourceSensorStateCurrent(""))

          //
          break
        }
      }
    }
  }
}

/**
 * When the user selects a target device, we need to load the actions for that
 * device in the background and pick a default action if possible.
 */
export const onChangeTargetDevice = createAsyncThunk(
  "createConnection/onChangeTargetDevice",
  async (deviceId: string, { dispatch }) => {
    dispatch(setTargetDevice(deviceId))
    let actions: Models.NamedDeviceAction[] = []

    // Load the actions for the device
    try {
      const response = await dispatch(
        loadDeviceActionsAsync({ deviceId }),
      ).unwrap()
      actions = response.items
    } catch (error) {
      captureException(error)
    }

    // Find the best action
    const defaultAction =
      actions.find((action) => action.expectedOutcome === "dry") ?? actions[0]
    if (defaultAction) {
      dispatch(setTargetActionId(defaultAction.id))
    }
    return Promise.resolve(undefined)
  },
)

export function getCreateConnectionErrors(state: RootState) {
  return validate(state)
}
export function getCreateConnectionErrorMessage(
  state: RootState,
  key: keyof Errors,
) {
  if (state.createDeviceConnection.showErrors) {
    return getCreateConnectionErrors(state)[key]
  }
  return undefined
}
export function getIsCreatingDevicePair(state: RootState) {
  const {
    sourceSensor,
    sourceSensorStateCurrent,
    sourceSensorStatePrevious,
    targetActionId,
  } = state.createDeviceConnection
  if (typeof targetActionId === "number") {
    const targetAction = state.namedDeviceActions.entities[targetActionId]
    if (
      sourceSensor === "reel" &&
      sourceSensorStateCurrent === "RS" &&
      sourceSensorStatePrevious === "RR" &&
      targetAction?.expectedOutcome === "dry"
    ) {
      return true
    }
  }
  return false
}

export const getSensorOptionsForSourceDevice = createCachedSelector(
  [Models.deviceConfiguration.selectById],
  (configuration) => {
    return configuration
      ? listConfigSensors(configuration).map((sensorName) => {
          return {
            label: formatSensorName(sensorName),
            value: sensorName,
          }
        })
      : []
  },
)(makeDeviceConfigurationCacheKey)
